"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, TrendingUp, Search, Users, Building2, CircleCheck as CheckCircle2, ArrowRight, Compass, ChartBar as BarChart3 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const searchMetrics = [{
  metric: "84%",
  label: "Local Search Intent",
  description: "of Chelmsford residents search online before contacting a local service business or visiting a commercial location."
}, {
  metric: "3.2x",
  label: "Conversion Velocity",
  description: "Higher lead conversion rate for businesses occupying top 3 positions in the Chelmsford Google Local Pack."
}, {
  metric: "62%",
  label: "Mobile Immediate Action",
  description: "of local searches carried out on mobile devices in Essex result in direct calls or venue visits within 24 hours."
}, {
  metric: "47%",
  label: "Uncaptured Demand",
  description: "of high-intent local search queries in Chelmsford are currently met with weak or unoptimized local competitors."
}];
const customerBehaviors = [{
  id: 1,
  title: "Proximity & Geo-Targeted Intent",
  description: "Chelmsford customers regularly use geo-modified phrases like 'near me', 'in Chelmsford', 'Essex B2B', or specific area names like Moulsham, Danbury, and Writtle.",
  badgeText: "High Buying Intent"
}, {
  id: 2,
  title: "Google Business Profile Reliance",
  description: "8 out of 10 prospective buyers validate local companies through Google Map Pack ratings, genuine customer reviews, and verified service listings before reaching out.",
  badgeText: "Trust Verification"
}, {
  id: 3,
  title: "Hyper-Specific Service Queries",
  description: "Modern searchers move beyond broad category searches, looking for specialized solutions, transparent pricing, and clear direct consultant or expert availability.",
  badgeText: "Specific Solutions"
}, {
  id: 4,
  title: "Multi-Touch Research Journeys",
  description: "Decision-makers cross-check local search rankings with detailed service pages, technical credentials, and verified local case studies before booking consultations.",
  badgeText: "Multi-Channel"
}];
const marketOpportunities = [{
  id: 1,
  title: "Dominating the Chelmsford Map Pack",
  detail: "Structuring localized NAP consistency, strategic citation building, and review acquisition strategies that propel your business into the top 3 map positions."
}, {
  id: 2,
  title: "Targeting High-Value Regional Terms",
  detail: "Capturing commercial search traffic across both Chelmsford city center and surrounding Essex growth corridors to drive qualified inbound phone calls and inquiries."
}, {
  id: 3,
  title: "Technical Local Schema Optimization",
  detail: "Implementing precise LocalBusiness and Service schema markup so search engines explicitly recognize your operational geographic footprint and service list."
}, {
  id: 4,
  title: "Content Aligned with Local User Intent",
  detail: "Creating authoritative, non-templated landing pages tailored to real Chelmsford buyers rather than bloated generic agency copy."
}];
export default function Chelmsford() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="chelmsfordlocalsearchlandscape" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/40 rounded-full filter blur-3xl -z-10 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/10 rounded-full filter blur-3xl -z-10 pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-sm bg-secondary text-secondary-foreground border border-accent/30">
            <MapPin className="w-3.5 h-3.5 text-accent mr-1.5 inline-block" />
            Chelmsford Local SEO Intelligence
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-primary font-default mb-6">
            Chelmsford Local Search Landscape & Market Opportunity
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
            As an independent SEO strategy consultant, I analyze real search patterns across Chelmsford and Essex.
            Understanding local buyer behavior is how I build tailored organic strategies that convert search demand into measurable revenue.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {searchMetrics.map((item, i) => <motion.div key={item.label} variants={itemVariants} data-index={i}>
              <Card className="h-full border border-border/70 shadow-sm bg-card transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-md">
                <CardHeader className="pb-2">
                  <div className="text-3xl sm:text-4xl font-extrabold text-primary font-default tracking-tight mb-1">
                    {item.metric}
                  </div>
                  <CardTitle className="text-base font-semibold text-foreground font-default">
                    {item.label}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm text-muted-foreground leading-normal">
                    {item.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-20">
          <div className="lg:col-span-5 space-y-6">
            <Badge variant="outline" className="border-accent text-accent font-medium px-3 py-1">
              <Compass className="w-3.5 h-3.5 mr-1.5 inline-block text-accent" />
              Customer Search Dynamics
            </Badge>
            
            <h3 className="text-2xl sm:text-3xl font-bold text-primary font-default tracking-tight">
              How Chelmsford Buyers Search & Select Services
            </h3>
            
            <p className="text-muted-foreground leading-relaxed font-default text-base">
              Local search behavior in Chelmsford has evolved beyond basic keyword lookups.
              Potential clients expect instant relevance, verified local reputation, and clear technical transparency.
            </p>

            <div className="p-6 rounded-xl bg-secondary/30 border border-border/80 space-y-3">
              <div className="flex items-center gap-2 font-semibold text-foreground font-default">
                <Building2 className="w-5 h-5 text-primary" />
                <span>Direct Consultant Advantage</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                When you work with me, I perform direct, manual local query analysis instead of relying on generic automated tool reports. You work directly with Yassine Essaadi — no junior account managers or generic templates.
              </p>
            </div>

            <div className="pt-2">
              <Link to="/ContactPage">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                  Book a Free SEO Consultation
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7">
            <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
            once: true,
            margin: "-50px"
          }} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {customerBehaviors.map((behavior, i) => <motion.div key={behavior.id} variants={itemVariants} data-index={i}>
                  <Card className="h-full border border-border/80 bg-card shadow-xs transition-[border-color,box-shadow] duration-300 hover:border-primary/30">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="p-2 rounded-lg bg-secondary text-accent font-semibold text-xs">
                          0{behavior.id}
                        </span>
                        <Badge variant="secondary" className="bg-secondary text-foreground text-xs font-normal">
                          {behavior.badgeText}
                        </Badge>
                      </div>
                      <CardTitle className="text-base font-semibold text-foreground font-default">
                        {behavior.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {behavior.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>)}
            </motion.div>
          </div>
        </div>

        <Separator className="my-16 bg-border" />

        <div className="space-y-10">
          <div className="text-center max-w-2xl mx-auto space-y-3">
            <h3 className="text-2xl sm:text-3xl font-bold text-primary font-default">
              Capitalizing on the Local Search Opportunity
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base font-default">
              My bespoke SEO framework turns local search bottlenecks into sustainable business growth.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          margin: "-50px"
        }} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {marketOpportunities.map((opp, i) => <motion.div key={opp.id} variants={itemVariants} data-index={i}>
                <div className="p-6 rounded-xl bg-card border border-border shadow-xs flex items-start gap-4 transition-[border-color,box-shadow] duration-300 hover:border-primary/40">
                  <div className="p-2.5 rounded-lg bg-secondary text-primary shrink-0 mt-0.5">
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                  </div>
                  <div className="space-y-1.5">
                    <h4 className="text-lg font-semibold text-foreground font-default">
                      {opp.title}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed font-default">
                      {opp.detail}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>

          <div className="mt-12 p-8 rounded-2xl bg-secondary/40 border border-accent/20 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-2 text-center md:text-left">
              <h4 className="text-xl font-bold text-primary font-default">
                Ready to capture your share of Chelmsford organic searches?
              </h4>
              <p className="text-sm text-muted-foreground font-default max-w-xl">
                I will personally audit your current local visibility, highlight quick wins, and build a tailored roadmap for your business.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <Link to="/ContactPage">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  Book Free Consultation
                </Button>
              </Link>
              <Link to="/CaseStudiesPage">
                <Button variant="outline" size="lg" className="border-border text-foreground hover:bg-accent hover:text-accent-foreground">
                  Explore Case Studies
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}